import CustomButton from "@Common/CustomButton";
import { ManualReservationWrapper } from "./styles";
import { useTranslation } from "react-i18next";
import BookingInformationForm from "./bookingInformationForm";
import PricingInformationForm from "./pricingInformationForm";
import GustInformationForm from "./gustInformationForm";
import { useCallback, useContext, useMemo, useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import CustomLoading from "../../components/CustomLoading";
import dayjs from "dayjs";
import {
  APIS_KEYS,
  usePostManualReservation,
  useGetUserDetail,
} from "@services";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastMessageContext } from "@context/toast-message-cotnext";
import ReviewForm from "./reviewForm";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";
import { useSetRecoilState } from "recoil";
import { isManualReservationOpen } from "@store/index";
import { useQueryClient } from "@tanstack/react-query";
import { SaveButton } from "./bookingInformationForm/styles";
import useBreakPoinst from "@hooks/useBreakPoinst";
export interface IFormManualReservation {
  booking_information: {
    propertyId: string;
    propertyName: string;
    checkIn: string | Date;
    checkOut: string | Date;
    isUserPaymentActive: boolean;
    nightPrice: number;
  };
  pricing_information: {
    nightPrice: number;
    totalPrice: number;
  };
  gust_information: {
    firstName: string;
    lastName: string;
    guestPhoneNumber: string;
    guestEmail: string;
    isAutoAccess: boolean;
  };
}
interface ManualReservationProps {
  preselectedProperty?: {
    propertyId: string;
    propertyName: string;
  };
  onClose?: () => void;
}

export default function ManualReservation({ preselectedProperty, onClose }: ManualReservationProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isManualReservationOpenState = useSetRecoilState(
    isManualReservationOpen
  );
  const { isPhone } = useBreakPoinst();
  const { mutateAsync: mutatePostManualReservation } =
    usePostManualReservation();
  const { showSuccess } = useContext(ToastMessageContext);
  const { data: userData } = useGetUserDetail();

  const [isloaderState, setIsloaderState] = useState(false);

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [activeTab, setActiveTab] = useState<1 | 2 | 3 | 4>(1);
  const queryClient = useQueryClient();
  const tabs = useMemo(() => [
    {
      label: t("MANUALRESERVATION.RESERVATIONINFORMATION"),
      value: 1,
    },
    {
      label: t("MANUALRESERVATION.PRICINGDATA"),
      value: 2,
    },
    {
      label: t("MANUALRESERVATION.GUESTDATA"),
      value: 3,
    },
  ], [t]);

  const defaultValues = useMemo(() => ({
    booking_information: {
      propertyId: preselectedProperty?.propertyId || "",
      propertyName: preselectedProperty?.propertyName || "",
      checkIn: "",
      checkOut: "",
      isUserPaymentActive: false,
    },
    pricing_information: {
      nightPrice: 0,
      totalPrice: 0,
    },
    gust_information: {
      firstName: "",
      lastName: "",
      guestPhoneNumber: "",
      guestEmail: "",
      isAutoAccess: false,
    },
  }), [preselectedProperty]);

  const methods = useForm<IFormManualReservation>({
    mode: "all",
    defaultValues,
  });

  // Initialize toggle state when user data is loaded
  useEffect(() => {
    if (
      userData?.result &&
      (userData.result as any)?.isUserPaymentActive !== undefined
    ) {
      const initialValue = (userData.result as any).isUserPaymentActive;
      methods.setValue("booking_information.isUserPaymentActive", initialValue);
    }
  }, [userData, methods]);

  const onSubmit = useCallback(async (value: IFormManualReservation) => {
    const { booking_information, pricing_information, gust_information } =
      value;

    if (
      !booking_information.propertyId ||
      !booking_information.checkIn ||
      !booking_information.checkOut
    ) {
      setActiveTab(1);
      return;
    }

    if (
      !pricing_information.nightPrice ||
      pricing_information.nightPrice <= 0 ||
      !pricing_information.totalPrice ||
      pricing_information.totalPrice <= 0
    ) {
      setActiveTab(2);
      return;
    }

    if (
      !gust_information.firstName ||
      !gust_information.lastName ||
      !gust_information.guestPhoneNumber ||
      !gust_information.guestEmail
    ) {
      setActiveTab(3);
      return;
    }
    if (activeTab !== 4) {
      setActiveTab(4);
      return;
    }
    setIsloaderState(true);
    const responseData = {
      id: 0,
      propertyId: Number(value?.booking_information?.propertyId),
      propertyName: value?.booking_information?.propertyName,
      checkIn: dayjs(value?.booking_information.checkIn).format("YYYY-MM-DD"),
      checkOut: dayjs(value?.booking_information.checkOut).format("YYYY-MM-DD"),
      totalPrice: Number(value?.pricing_information?.totalPrice),
      firstName: value?.gust_information?.firstName,
      lastName: value?.gust_information?.lastName,
      guestPhoneNumber: value?.gust_information?.guestPhoneNumber,
      guestEmail: value?.gust_information?.guestEmail,
      isAutoAccess: false,
      notes: "",
      isDirectPaymentAccepted: value?.booking_information?.isUserPaymentActive,
    };
    const response = await mutatePostManualReservation({ ...responseData });

    if (response.status === 201 || response.status === 204) {
      setIsloaderState(false);
      if (location.pathname === "/calendar") navigate("/calendar");
      showSuccess(t("MANUALRESERVATION.RESERVATIONHASBEENSUCCESSFULLYSAVED"));
      queryClient.invalidateQueries({ queryKey: [APIS_KEYS.GETCALENDERDATA] });
      queryClient.invalidateQueries({
        queryKey: [APIS_KEYS.GETLISTINGMANAGEMENT],
      });
      isManualReservationOpenState(false);
    }
    setIsloaderState(false);
  }, [mutatePostManualReservation, navigate, showSuccess, t, queryClient, isManualReservationOpenState, activeTab]);

  // Memoize tab click handler
  const handleTabClick = useCallback(async (tabValue: 1 | 2 | 3) => {
    const sectionName =
      activeTab === 1
        ? "booking_information"
        : activeTab === 2
          ? "pricing_information"
          : "gust_information";

    const isValid = await methods.trigger(sectionName as any);

    if (isValid) {
      setActiveTab(tabValue);
    }
  }, [activeTab, methods]);

  // Memoize navigation click handler
  const handleNavigationClick = useCallback(() => {
    if (activeTab === 4) {
      setActiveTab(1);
    } else {
      isManualReservationOpenState(false);
      onClose?.();
    }
  }, [activeTab, isManualReservationOpenState, onClose]);

  return (
    <ManualReservationWrapper isRTL={language} $isPhone={isPhone}>
      <div>
        {isloaderState && <CustomLoading />}
        <div className="containerHeader">
          <div>
            <div className="navigate">
              <button
                type="button"
                onClick={handleNavigationClick}
              >
                {language === "ar" ? <FaArrowLeftLong /> : <FaArrowRightLong />}
              </button>
            </div>
            <div className="containerInfo">
              <p>
                {t(
                  `MANUALRESERVATION.${activeTab === 4 ? "REVIEWTHEDATAANDCONFIRMTHERESERVATION" : "CREATEMANUALRESERVATION"}`
                )}
              </p>
            </div>
          </div>
        </div>
        {activeTab !== 4 && (
          <div className="containerTabs">
            {tabs.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`${item.value === activeTab ? "activeButton" : "normal"}`}
                onClick={() => handleTabClick(item.value as 1 | 2 | 3)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
        <div>
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              id="formManualReservation"
            >
              {activeTab === 1 && <BookingInformationForm />}
              {activeTab === 2 && <PricingInformationForm />}
              {activeTab === 3 && <GustInformationForm />}
              {activeTab === 4 && <ReviewForm />}
            </form>
          </FormProvider>

          {activeTab !== 4 ? (
            <SaveButton
              type="submit"
              form="formManualReservation"
              $isPhone={isPhone}
            >
              {t("MANUALRESERVATION.SAVE")}
            </SaveButton>
          ) : (
            <CustomButton
              label={t("MANUALRESERVATION.CONFIRMTHERESERVATION")}
              withBG
              style={{ width: "100%" }}
              customPadding="10px 27px"
              type="submit"
              form="formManualReservation"
            />
          )}
        </div>
      </div>
    </ManualReservationWrapper>
  );
}
